import { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { postLogDetails } from "../../../Services/APIs/appDetails";
import { generateRandomAlphaNumeric } from "../../../Utils/alphaNumeric";
import welcomeTeeth from "../../../assets/illustrations/welcomeTeeth.png";
import toothlensLogo from "../../../assets/illustrations/Toothlens_logo.png";

const COMPANY_BUCKET_MAP = {
  "ab": "ab", 
  "tl-star": "starhealth",
  "care": "care",
  "plum": "pluminsurance"
};

const VALID_CAMERA_SIDES = ["front", "back"];

const PRESERVED_KEYS = [
  "usertpi",
  "cameraSide", 
  "company", 
  "companyBucket", 
  "scanType", 
  "isAutomated",
  "reloaded-star"
];

const LandingPage = () => {
  const { company = "tl-star", cameraside = "front" } = useParams();
  const navigate = useNavigate();
  const { images } = useSelector((state) => state.fullScan);
  const storedUid = JSON.parse(localStorage.getItem("usertpi") || '{"usertpi": ""}');
  const [uid, setUid] = useState(storedUid.usertpi);
  const resolvedCompany = COMPANY_BUCKET_MAP.hasOwnProperty(company) ? company : "tl-star";
  const resolvedCameraSide = VALID_CAMERA_SIDES.includes(cameraside) ? cameraside : "front";
  const bucket = COMPANY_BUCKET_MAP[resolvedCompany];

  useEffect(() => {
    cleanLocalStorageWithPreservation();
    localStorage.setItem("isAutomated", "true");
    localStorage.setItem("scanType", "tl-star");
    const isReloaded = JSON.parse(localStorage.getItem("reloaded-star") || "false");
    if (!isReloaded) {
      localStorage.setItem("reloaded-star", "true");
      window.location.reload();
      return;
    }
    if (!uid || images.length === 0) {
      generateUserInfo();
    }
  }, []);

  const cleanLocalStorageWithPreservation = () => {
    const backup = {};
    PRESERVED_KEYS.forEach(key => {
      const value = localStorage.getItem(key);
      if (value !== null) {
        backup[key] = value;
      }
    });
    localStorage.clear();
    Object.entries(backup).forEach(([key, value]) => {
      localStorage.setItem(key, value);
    });
  };

  const generateUserInfo = useCallback(() => {
    // Generate 4 random alphabetic letters
    const letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const alphaPrefix = Array.from({ length: 4 }, () => 
      letters.charAt(Math.floor(Math.random() * letters.length))
    ).join('');
    
    // Generate 8 digit number using timestamp + random for uniqueness
    const timestamp = Date.now().toString();
    const randomSuffix = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
    const numericPart = (timestamp.slice(-5) + randomSuffix).slice(0, 8).padStart(8, '0');
    
    const uniqueId = alphaPrefix + numericPart;
    localStorage.setItem("usertpi", JSON.stringify({ usertpi: uniqueId }));
    localStorage.setItem("companyBucket", JSON.stringify({ companyBucket: bucket }));
    localStorage.setItem("cameraSide", resolvedCameraSide);
    localStorage.setItem("company", resolvedCompany);
    setUid(uniqueId);
    postLogDetails({ 
      uid: uniqueId, 
      app: `${resolvedCompany} of side ${resolvedCameraSide}`, 
      payLoad: `visited landing page` 
    });
  }, [resolvedCompany, resolvedCameraSide, bucket]);

  const handleNewScan = () => {
    cleanLocalStorageWithPreservation();
    localStorage.setItem("isAutomated", "true");
    localStorage.setItem("reloaded-star", "true");
    localStorage.setItem("scanType", "tl-star");
    generateUserInfo();
    navigate('/dental-form');
  };

  return (
    <div className="min-h-screen bg-white px-4 py-6 flex flex-col text-gray-800 font-sans">
      <h1 className="text-2xl font-bold text-center text-blue-900 mb-1">Welcome to ToothLens</h1>
      <h2 className="text-lg font-medium text-center text-gray-700 mb-4">Let's Get You Started!</h2>

      <p className="text-sm text-center text-gray-600 mb-6">
        Before you begin, please ensure you have the following information and documents ready. 
        The entire process will take just a few minutes.
      </p>

      <h3 className="text-md font-semibold mb-3 text-center">What You'll Need to Complete:</h3>

      <ul className="text-sm text-gray-800 space-y-4 px-2">
        <li>
          <span className="font-bold">① Patient Personal Information</span><br />
          <span className="text-gray-600">(Name, DOB, Contact Info, Address, etc.)</span>
        </li>
        <li>
          <span className="font-bold">② KYC Details</span><br />
          <span className="text-gray-600">(Identity verification using Aadhaar and PAN Card)</span>
        </li>
        <li>
          <span className="font-bold">③ Nominee Details</span><br />
          <span className="text-gray-600">(Emergency contact and relationship information)</span>
        </li>
        <li>
          <span className="font-bold">④ Medical Status</span><br />
          <span className="text-gray-600">(Current health conditions, allergies, ongoing medications)</span>
        </li>
        <li>
          <span className="font-bold">⑤ Health Agreement</span><br />
          <span className="text-gray-600">(Digital consent to proceed with our dental screening)</span>
        </li>
      </ul>

      <div className="mt-6 text-sm">
        <p className="font-semibold text-gray-800 mb-1">Prepare to Capture:</p>
        <p className="text-gray-600">• A clear photo of your face and teeth</p>
      </div>
      
      <div className="mt-6 bg-yellow-100 border-l-4 border-yellow-500 text-sm text-gray-800 p-4 rounded">
        <p className="mb-2 font-medium">Please Note:</p>
        <ul className="list-disc list-inside space-y-1">
          <li>Provide accurate and relevant medical and dental history.</li>
          <li>False or incomplete information can lead to denial of coverage or non-issue of the policy.</li>
        </ul>
      </div>
      
      <div className="w-full flex flex-col justify-center flex-grow py-2">
        {localStorage.getItem("isScanCompleted") ? (
          <div>
            <p className="text-lg text-green-600 font-medium text-center my-3">
              You have successfully completed your scan 
            </p>
            <button
              onClick={handleNewScan}
              className="bg-black rounded text-white w-full h-11 font-semibold text-base"
            >
              Take New Scan
            </button>
          </div>
        ) : (
          <button
            onClick={handleNewScan}
            className="bg-black rounded text-white w-full h-11 font-semibold text-base"
          >
            Scan
          </button>
        )}
      </div>

      <div className="w-full px-4 mb-4">
        <p className="flex items-center justify-center">
          <span className="opacity-60">Powered by</span>
          <img
            src={toothlensLogo}
            alt="ToothLens Logo"
            className="ml-2 w-24"
          />
        </p>
      </div>
    </div>
  );
};

export default LandingPage;